import { Stack, Typography } from "@mui/material";
import { EMPTY_VALUE_PLACEHOLDER } from "@shared";

import { CounselorResponse } from "@/api";
import { EllipsisText } from "@/common/components/ellipsis-text/EllipsisText";
import { createColumns } from "@/common/components/table/DataTable.utils";
import { UserAvatar } from "@/common/components/user-avatar/UserAvatar";
import { useCounselorsQuery } from "@/common/hooks/useCounselorsQuery";
import { useTranslation } from "@/translations";

import { formatCounselorRoles, formatListName, primaryCounselorRole } from "../CounselorList.utils";

export function useCounselorList() {
  const { t } = useTranslation();

  const { data, isLoading } = useCounselorsQuery();

  const columns = createColumns<CounselorResponse>([
    {
      field: "lastName",
      headerName: t("counselors.table.counselor"),
      flex: 2,
      renderCell: (params) => {
        const row = params.row as CounselorResponse;
        return (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ height: "100%" }}>
            <UserAvatar label={row.userCode} role={primaryCounselorRole(row.roles)} />
            <Stack sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600}>
                {formatListName(row)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatCounselorRoles(row.roles, t)}
              </Typography>
            </Stack>
          </Stack>
        );
      },
    },
    {
      field: "userCode",
      headerName: t("counselors.table.kzz"),
      flex: 1,
      renderCell: (params) => <EllipsisText value={params.value as string} variant="body2" fontWeight={600} />,
    },
    {
      field: "team",
      headerName: t("counselors.table.team"),
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <EllipsisText value={(params.value as string | null) ?? EMPTY_VALUE_PLACEHOLDER} variant="body2" />
      ),
    },
    {
      field: "phoneExtension",
      headerName: t("counselors.table.phoneExtension"),
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <EllipsisText value={(params.value as string | null) ?? EMPTY_VALUE_PLACEHOLDER} variant="body2" />
      ),
    },
    {
      field: "email",
      headerName: t("counselors.table.email"),
      flex: 1.5,
      sortable: false,
      renderCell: (params) => (
        <EllipsisText value={(params.value as string | null) ?? EMPTY_VALUE_PLACEHOLDER} variant="body2" />
      ),
    },
    {
      field: "weeklyHours",
      headerName: t("counselors.table.weeklyHours"),
      width: 120,
      align: "right",
      headerAlign: "right",
      sortable: false,
      renderCell: (params) => {
        const hours = (params.row as CounselorResponse).weeklyHours;
        return <EllipsisText value={hours != null ? `${hours} h` : EMPTY_VALUE_PLACEHOLDER} variant="body2" />;
      },
    },
  ]);

  return { data: data ?? [], isLoading, columns };
}
